/**
 * Google Enhanced Ecommerce Object
 *
 * Promos logic and functionality for Google Enhanced Ecommerce.
 * https://confluence.int.plus.net/display/DIGITAL/Google+Enhanced+Ecommerce
 */
const promos = {

    /**
     * This section now gets quite complex as it relies upon the elementStateReporter.js
     *
     * Promotions only fire an event when in view and it will look for the section with the data-section for its
     * positioning
     *
     * We also hash the html to create a unique identifier for the ID meaning we can track autogenerated changes
     * i.e. CMS
     *
     * Code example for html attribute:
     * data-ecom-promotion='{"name": "Great Mobile Plans", "creative": "Joe pointing to mobile blue"}'
     * Code example for section: data-section="Header"
     *
     */
    initialise: () => {

        if (
            gaEcommerce.promos.isInitialised
            || !gaEcommerce.core.checkStorageCapabilities()
        ) return;

        gaEcommerce.util.initHashCodePrototype();

        // Throttle these events as not to overload the client
        window.addEventListener(
            'resize',
            gaEcommerce.util.throttle(gaEcommerce.promos.sendVisiblePromos, 250)
        );
        window.addEventListener(
            'scroll',
            gaEcommerce.util.throttle(gaEcommerce.promos.sendVisiblePromos, 250)
        );

        gaEcommerce.promos.sendVisiblePromos();

        const observer = new MutationObserver(gaEcommerce.util.throttle(gaEcommerce.promos.sendVisiblePromos, 250)),
              options = {
                  attributeFilter: ['class', 'style'],
                  attributes: true,
                  childList: true,
                  subtree: true
              };

        observer.observe(document.getElementsByTagName('body')[0], options);

        gaEcommerce.promos.isInitialised = true;
    },

    /**
     * Find all visible promos and send them to GA
     *
     * @returns {void}
     */
    sendVisiblePromos: () => {

        let promotions = document.querySelectorAll('[data-ecom-promotion]:not([data-promotion-seen])');

        promotions = [...promotions].filter((item) => 
            gaEcommerce.util.isVisible(item)
        );

        if (!promotions?.length) {
            return;
        }

        const visiblePromotions = gaEcommerce.promos.getInViewportPromotions(promotions);

        if (visiblePromotions.length) {
            gaEcommerce.core.sendData(
                {promoView: {promotions: visiblePromotions}}, 'promoView'
            );
        }
    },

    /**
     * Loop through all promotions to see what is visible and in the viewport
     *
     * @param {array|nodelist} promotions
     * @returns {array} visible promotions
     */
    getInViewportPromotions: (promotions) => {

        const visiblePromotions = [];

        promotions.forEach((item) => {
            if (!gaEcommerce.util.isSeen(item)) {
                return;
            }

            gaEcommerce.promos.updatePromoElement(item);

            visiblePromotions.push(
                gaEcommerce.promos.getPromoGaData(
                    gaEcommerce.util.parseJsonObj(item.dataset.ecomPromotion),
                    item
                )
            );
        });

        return visiblePromotions;
    },

    /**
     * Update promo data attributes
     * - Create an id for the promo
     * - Mark promo as seen
     *
     * @param {object} promotion DOM Element
     * @returns {void}
     */
    updatePromoElement: (promotion) => {
        const data = gaEcommerce.util.parseJsonObj(promotion.dataset.ecomPromotion);
        data.id = promotion.innerHTML.hashCode();

        // Update the Data attribute with our new ID
        promotion.dataset.ecomPromotion = JSON.stringify(data);
        promotion.dataset.promotionSeen = '1';
    },

    /**
     * Get the GA data from the element data
     *
     * @param {object} data
     * @param {object} promotion DOM Element
     *
     * @returns {object}
     */
    getPromoGaData: (data, promotion) => {
        // Create an object of visible promotions
        const visiblePromotion = {};

        // Only send what's necessary
        if (typeof data.id !== 'undefined') {
            visiblePromotion.id = data.id;
        }

        if (typeof data.name !== 'undefined') {
            visiblePromotion.name = data.name
        }

        if (typeof data.creative !== 'undefined') {
            visiblePromotion.creative = data.creative;
        }

        // Journey name
        if (Object.keys(visiblePromotion).length) {
            visiblePromotion['dimension48'] = gaEcommerce.core.getJourney();
        }

        // Get the element from where it is in the view
        const positionText = gaEcommerce.promos.getPromotionPosition(promotion);

        if (typeof positionText !== 'undefined') {
            visiblePromotion.position = positionText;
        }

        return visiblePromotion;
    },

    /**
     * promotionClick
     *
     * @param {object} promotion DOM element
     * @returns {void}
     */
    promotionClick: (promotion) => {

        // If the element we've clicked doesn't have a data-ecom-promotion attr, find the closest that does
        if (!promotion.dataset.ecomPromotion) {
            promotion = promotion.closest('[data-ecom-promotion]');
        }

        if (promotion) {
            const promotionData = gaEcommerce.promos.getPromoGaData(
                gaEcommerce.util.parseJsonObj(promotion.dataset.ecomPromotion), promotion
            );
            gaEcommerce.core.sendData(
                {promoClick: {promotions: [promotionData]}}, 'promotionClick'
            );
        }
    },

    /**
     * getPromotionPosition
     *
     * @param {object} promotion DOM element
     * @returns {string}
     */
    getPromotionPosition: (promotion) => {
        const position = promotion.dataset.ecomPosition
                || promotion.dataset.interactionModulePosition;

        const parentSection = promotion.closest('[data-section]');

        let section = 'page';

         if (parentSection) {
             section = parentSection.dataset.section;
         }

        return (section + ' - ' + position).toLowerCase();
    },

    /**
     * Get an object that represents all promotions available
     *
     * @returns {object}
     */
    getPromotions: () => {
        if (typeof gaEcommerce.promos.promos === 'undefined') {
            gaEcommerce.promos.promos = {};

            document.querySelectorAll('[data-discount]').each((item,) => {
                const discounts = gaEcommerce.util.parseJsonArray(item.dataset.discount);

                discounts.map(function (discount) {
                    gaEcommerce.promos.promos[discount.id] = discount;
                });
            });
        }

        return gaEcommerce.promos.promos;
    },

    /**
     * Get a single promotion by coupon code
     *
     * @param {string} id
     * @returns {null|object}
     */
    getPromoById: (id) => {
        const promos = gaEcommerce.promos.getPromotions();

        return promos[id]
            ? gaEcommerce.promos.getPromotions()[id]
            : null
    },

    /**
     * Check if a promo is a bundle promo
     *
     * @param {object} promo
     * @returns {boolean}
     */
    isBundlePromo: (promo) => {
        let isBundle = false;

        if (typeof promo.data.offerElements[0].requiredCharacteristics !== 'undefined') {
            promo.data.offerElements[0].requiredCharacteristics.map(
                (characteristic) => {
                    if (characteristic.name === 'C_PhoneLineCallChargesTariff') {
                        isBundle = true;
                    }
                }
            );
        }

        return isBundle;
    },

    /**
     * Add a bundle to the basket
     *
     * @param {object} bundle
     * @param {string} interaction
     */
    addBundle: (bundle, interaction) => {

        let ids,
            names,
            phone,
            callPlanProductElement,
            callPlanProduct,
            lineRentalProduct;

        const productsToAdd = [];

        if (bundle.id.indexOf('|') === -1) {
            ids = [bundle.id];
            names = [bundle.name];
        } else {
            ids = bundle.id.split("|");
            names = bundle.name.split("|");
            phone = gaEcommerce.util.mergeObject({}, bundle, {id: ids[1], name: names[1]});
            delete phone['category'];
            delete phone['price'];
            callPlanProductElement = gaEcommerce.core.getProductElementById(phone.id);
            callPlanProduct = gaEcommerce.util.parseJsonObj(callPlanProductElement.dataset.ecomProduct);
            productsToAdd.push(phone);
        }

        const broadband = gaEcommerce.util.mergeObject({}, bundle, {id: ids[0], name: names[0]});

        // We want to unset the categories, as these will be 'dual play' categories, rather than the correct ones
        delete broadband['category'];
        delete broadband['price'];

        const lineRentalElement = gaEcommerce.core.getProductElementById('Plusnet_Services_Phone_Line-Rental');

        if (lineRentalElement) {
            const lineRentalInput = lineRentalElement.querySelector('input');
            // If line rental input is disabled, this is a solus "package"
            if (lineRentalInput && !lineRentalInput.disabled) {
                lineRentalProduct = gaEcommerce.util.parseJsonObj(lineRentalElement.dataset.ecomProduct);
            }
        }

        broadband.price = parseFloat(bundle.price);

        if (callPlanProduct) {
            broadband.price -= parseFloat(callPlanProduct.price);
        }

        if (lineRentalProduct) {
            broadband.price -= parseFloat(lineRentalProduct.price);
        }

        broadband.price = broadband.price.toFixed(2);
        productsToAdd.push(broadband);

        gaEcommerce.basket.addProductData(productsToAdd, interaction);
    },

    /**
     * Update broadband discounts if necessary
     *
     * @param {object} gaBroadbandProduct
     * @returns {void}
     */
    updateBroadbandDiscount: (gaBroadbandProduct) => {

        const basketBroadbandProduct = gaEcommerce.basket.findBasketBroadband(gaBroadbandProduct);

        if (basketBroadbandProduct === null) {
            return;
        }

        const shouldHaveDiscount = typeof basketBroadbandProduct.discount.discountId !== 'undefined',
              hasDiscount = typeof gaBroadbandProduct.coupon !== 'undefined' && gaBroadbandProduct.coupon.length > 0;

        // If we shouldn't have discount, and we don't, then just bail
        if (!shouldHaveDiscount && !hasDiscount) {
            return;
        }

        // If we shouldn't have a discount, and we do have a discount, then remove it
        if (!shouldHaveDiscount && hasDiscount) {
            gaEcommerce.promos.removeDiscount(gaBroadbandProduct.coupon);
            return;
        }

        // If we should have a discount, but don't, then we need to add discount
        if (shouldHaveDiscount && !hasDiscount) {
            gaEcommerce.promos.addDiscount(
                basketBroadbandProduct.discount.discountId,
                basketBroadbandProduct.discount.discountPrice
            );
            return;
        }

        // If we should have discount, and we do, then we need to check if it's the correct discount
        if (shouldHaveDiscount && hasDiscount) {
            const shouldHaveDiscountCode = basketBroadbandProduct.discount.discountId,
                  hasDiscountCode = gaBroadbandProduct.coupon;

            // If we have the correct discount, then just bail
            if (shouldHaveDiscountCode === hasDiscountCode) {
                return;
            }

            // If we have the wrong discount, then we need to swap it
            gaEcommerce.promos.switchDiscount(
                hasDiscountCode,
                shouldHaveDiscountCode,
                basketBroadbandProduct.discount.discountPrice
            );
        }
    },

    /**
     * Add a discount code
     *
     * @param {string} coupon
     * @param {number} price
     *
     * @returns {void}
     */
    addDiscount: (coupon, price) => {

        const itemsToRemove = [],
              itemsToAdd = [],
              broadbandRule = {
                  items: [{ category: gaEcommerce.rules.matchBroadband() }],
                  modifications: {
                      coupon: coupon,
                      price: price
                  }
              };

        gaEcommerce.rules.processUpdateRule(broadbandRule, itemsToRemove, itemsToAdd);

        const discount = gaEcommerce.promos.getPromoById(coupon);

        if (discount !== null && gaEcommerce.promos.isBundlePromo(discount)) {
            const rule = {
                items: [{ category: gaEcommerce.rules.matchCallPlan() }],
                modifications: { coupon: coupon }
            };
            gaEcommerce.rules.processUpdateRule(rule, itemsToRemove, itemsToAdd);
        }
        gaEcommerce.basket.processItems(itemsToAdd, itemsToRemove, 'non-interaction', false);
    },

    /**
     * Switch a discount from the fromCoupon to the toCoupon
     *
     * @param {string} fromCoupon
     * @param {string} toCoupon
     * @param {number} price
     *
     * @returns {void}
     */
    switchDiscount: (fromCoupon, toCoupon, price) => {

        const itemsToRemove = [],
              itemsToAdd = [],
              broadbandRule = {
                  items: [{ category: gaEcommerce.rules.matchBroadband() }],
                  modifications: {
                      coupon: toCoupon,
                      price: price
                  }
              };
        gaEcommerce.rules.processUpdateRule(broadbandRule, itemsToRemove, itemsToAdd);

        const toDiscount = gaEcommerce.promos.getPromoById(toCoupon);
        let phoneRule;

        // If the new discount is a bundle, then we can replace all phone to have the new coupon
        if (gaEcommerce.promos.isBundlePromo(toDiscount)) {
            phoneRule = {
                items: [{ category: gaEcommerce.rules.matchCallPlan() }],
                modifications: { coupon: toCoupon }
            };
        } else {
            phoneRule = {
                items: [{
                    category: gaEcommerce.rules.matchCallPlan(),
                    coupon: fromCoupon
                }],
                modifications: {coupon: ''}
            };
        }

        gaEcommerce.rules.processUpdateRule(phoneRule, itemsToRemove, itemsToAdd);
        gaEcommerce.basket.processItems(itemsToAdd, itemsToRemove, 'non-interaction', false);
    },

    /**
     * Remove a discount
     *
     * @param {string} coupon
     * @returns {void}
     */
    removeDiscount: (coupon) => {
        const itemsToRemove = [],
              itemsToAdd = [],
              rule = {
                  items: [{coupon: coupon}],
                  modifications: {}
              };

        gaEcommerce.rules.processReplaceRule(rule, itemsToRemove, itemsToAdd);
        gaEcommerce.basket.processItems(itemsToAdd, itemsToRemove, 'non-interaction', false);
    }
};

export default promos;
